import { SelectOptions } from "core/models/Settings";
import { maxLength } from "core/utils/Normalizer";
import { useCallback, useEffect, useRef } from "react";
import {
  Field,
  FieldArray,
  formValueSelector,
  getFormValues,
  InjectedFormProps,
  reduxForm,
  WrappedFieldProps,
} from "redux-form";
import { Button, Form, Input, Toggle } from "rsuite";
import InputWithLabel from "shared/components/redux-form/InputWithLabel";
import Select from "shared/components/redux-form/Select";
import Arrow from "shared/Icons/Arrow";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import validate from "./validate";
import BracketRoundFields from "./BracketRound";
import { BracketRounds } from "core/models/Bracket";

export interface StepImage {
  file: File | null;
  preview: string;
}
interface AddBracketFormProps {
  bracketRounds?: BracketRounds | null;
  selectedBracketStyle: string;
  selectedEligibleParticipants: string;
  selectedBracketRounds: any;
  selectedBracketName: string;
  participantCount: number;
  onProccedSeed(): void;
  isEditMode: boolean;
}

const ELIGIBLE_PARTICIPANTS: SelectOptions[] = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Checked In",
    value: "checkedIn",
  },
];

export const BRACKET_STYLE: SelectOptions[] = [
  {
    label: "Single Elimination",
    value: "Single Elimination",
  },
  {
    label: "Double Elimination",
    value: "Double Elimination",
  },
];

export const BEST_OF_OPTIONS = [
  { label: "Best Of 1", value: "BO1" },
  { label: "Best Of 3", value: "BO3" },
  { label: "Best Of 5", value: "BO5" },
];
type FinalProps = InjectedFormProps<{}, AddBracketFormProps> &
  AddBracketFormProps;

const handleSubmitFail = () => {
  setTimeout(() => {
    const firstElement = document.getElementsByClassName(
      "rs-error-message-wrapper"
    )[0];
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
};

const AddBracketForm = ({
  bracketRounds,
  selectedBracketStyle,
  selectedEligibleParticipants,
  participantCount,
  selectedBracketRounds,
  selectedBracketName,
  handleSubmit,
  onProccedSeed,
  initialize,
  change,
  initialized,
  submitFailed,
  isEditMode,
  reset,
  touch,
}: FinalProps) => {
  const history = useHistory();
  const prevBracketStyleRef = useRef<string>(selectedBracketStyle);

  useEffect(() => {
    if (!bracketRounds) return;
  
    const roundsKey = selectedEligibleParticipants === "all" ? "allTeams" : "checkedInTeams";
    const roundsData = bracketRounds[roundsKey];
  
    if (!roundsData) return;
  
    const bracketRoundsValues: any = {};
    Object.entries(roundsData).forEach(([sectionKey, rounds]: any) => {
      bracketRoundsValues[sectionKey] = rounds.map(
        (round: any, index: number) => ({
          roundName: round.roundName || "",
          matchFormat: round.matchFormat || "",
          roundSequence: round.roundSequence ?? index + 1,
        })
      );
    });
  
    const useFreshRounds = prevBracketStyleRef.current !== selectedBracketStyle;
  
    initialize({
      bracketRounds: useFreshRounds ? bracketRoundsValues : selectedBracketRounds || bracketRoundsValues,
      bracketName: selectedBracketName || "",
      eligibleParticipants: selectedEligibleParticipants || "all",
      bracketStyle: selectedBracketStyle || "Single Elimination",
    });
  }, [bracketRounds]);
  

  // useEffect(() => {
  //   if (bracketRounds) {
  //     const roundsKey =
  //       selectedEligibleParticipants === "all" ? "allTeams" : "checkedInTeams";

  //     setTimeout(() => {
  //       Object.entries(bracketRounds[roundsKey]).forEach(
  //         ([sectionKey, rounds]: any) => {
  //           rounds.forEach((round: any) => {
  //             try {
  //               touch(`${sectionKey}.${round.roundName}.roundName`);
  //               touch(`${sectionKey}.${round.roundName}.matchFormat`);
  //             } catch (err) {}
  //           });
  //         }
  //       );
  //     }, 0);
  //   }
  // }, [bracketRounds, selectedEligibleParticipants, touch]);

  const validateUploads = useCallback(() => {
    let isValid = true;

    return isValid;
  }, []);

  function handleForm(formData: any) {
    const isValid = validateUploads();
    if (!isValid) return;

    onProccedSeed();
  }

  function handleRedirection() {
    history.goBack();
  }

  useEffect(() => {
    if (submitFailed) {
      validateUploads();
    }
  }, [submitFailed, validateUploads]);

  return (
    <Form fluid onSubmit={handleSubmit(handleForm)}>
      <div className="add-bracket-form">
        <div className="add-bracket-form-title-wrapper">
          <span className="form-back-btn" onClick={handleRedirection}>
            <Arrow fontSize={32} />
          </span>
          <h4>{isEditMode ? "Edit Bracket" : "Create Bracket"}</h4>
        </div>
        <div className="section-wrapper">
          <div className="section-title">Create Bracket</div>
          <div className={clsx("bracket-grid-form-wrapper", "spacing")}>
            <Field
              name="bracketName"
              component={InputWithLabel}
              placeholder="Enter Bracket Name"
              label="Bracket Name"
              normalize={(value: string) => maxLength(value, 50)}
            />
            <Field
              cleanable={true}
              name="eligibleParticipants"
              component={Select}
              placeholder="Select"
              label="Eeligible Participants"
              options={ELIGIBLE_PARTICIPANTS}
            />
            <Field
              cleanable={true}
              name="bracketStyle"
              component={Select}
              placeholder="Select"
              label="Bracket Style"
              options={BRACKET_STYLE}
            />
            <div className="toggle-button-container">
              <p>Participant Count</p>
              <Input
                name="bracketName"
                placeholder="Enter Bracket Name"
                value={participantCount}
                disabled
              />
            </div>
            {selectedBracketStyle === "Single Elimination" && (
              <div className="toggle-button-container">
                <p>Enable third position</p>
                <Field
                  name="thirdPlaceMatch"
                  type="checkbox"
                  component={({ input }: WrappedFieldProps) => (
                    <div className="flex items-center gap-2">
                      <Toggle
                        checked={!!input.value}
                        onChange={(value) => input.onChange(value)}
                      />
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </div>
        {selectedBracketRounds && (
          <>
            {Object?.entries(selectedBracketRounds).map(
              ([sectionKey, rounds]) => (
                <div key={sectionKey} className="section-wrapper">
                  <div className="section-grid-wrapper">
                    <div className="section-title">{sectionKey}</div>
                    <FieldArray
                      name={`bracketRounds.${sectionKey}`}
                      component={BracketRoundFields}
                      props={{ sectionKey }}
                    />

                    {/* {Array.isArray(rounds) &&
                      rounds.map((round) => (
                        <>
                          <div
                            key={round.roundSequence}
                            className={clsx("section-sec-title", "spacing")}
                          >
                            Round {round?.roundSequence}
                          </div>
                          <div
                            className={clsx(
                              "bracket-grid-form-wrapper",
                              "spacing"
                            )}
                          >
                            <div className="input-container">
                              <Field
                                name={`${sectionKey}.${round.roundName}.roundName`}
                                component={InputWithLabel}
                                placeholder="Enter Round Name"
                                label={round.roundName}
                                normalize={(value: string) =>
                                  maxLength(value, 50)
                                }
                              />
                            </div>
                            <div className="input-container">
                              <Field
                                name={`${sectionKey}.${round.roundName}.matchFormat`}
                                component={Select}
                                placeholder="Select Best of it"
                                label="Match Format"
                                options={BEST_OF_OPTIONS}
                              />
                            </div>
                          </div>
                        </>
                      ))} */}
                  </div>
                </div>
              )
            )}
          </>
        )}
        <div className="game-submit-cta">
          <Button appearance="primary" type="submit">
            Proceed to Seed Players
          </Button>
        </div>
      </div>
    </Form>
  );
};

const AddBracket = reduxForm<{}, AddBracketFormProps>({
  form: "add-bracket",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
  keepDirtyOnReinitialize: true,
  onSubmitFail: handleSubmitFail,
})(AddBracketForm);
const selector = formValueSelector("add-bracket");

export default connect((state) => {
  const selectedBracketStyle = selector(state, "bracketStyle");
  const selectedEligibleParticipants = selector(state, "eligibleParticipants");
  const selectedBracketRounds = selector(state, "bracketRounds");
  const selectedBracketName = selector(state, "bracketName");

  return {
    selectedBracketStyle,
    selectedEligibleParticipants,
    selectedBracketRounds,
    selectedBracketName,
  };
})(AddBracket);
